import React, { useState } from 'react';
import { Card, Badge, Button, Carousel } from 'react-bootstrap';
import { BsHouseDoor, BsDropletFill, BsRulers, BsHeart, BsHeartFill, BsChevronLeft, BsChevronRight } from 'react-icons/bs';

interface Property {
  id: string;
  mlsNumber: string;
  price: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  images?: string[];
  thumbnails?: string[];
  matchScore?: number;
  daysOnMarket?: number;
}

interface PropertyCardWithCarouselProps {
  property: Property;
  onSave: (propertyId: string) => void;
  onView: (propertyId: string) => void;
  isSaved: boolean;
}

const PropertyCardWithCarousel: React.FC<PropertyCardWithCarouselProps> = ({
  property,
  onSave,
  onView,
  isSaved
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Get all available images
  const allImages = [
    ...(property.images || []),
    ...(property.thumbnails || [])
  ].filter((img, index, self) => self.indexOf(img) === index); // Remove duplicates
  
  // Limit to first 10 images for performance
  const displayImages = allImages.slice(0, 10);
  
  // If no images, use placeholder
  if (displayImages.length === 0) {
    displayImages.push('https://via.placeholder.com/400x300?text=No+Image+Available');
  }

  const handleSelect = (selectedIndex: number) => {
    setActiveIndex(selectedIndex);
  };

  return (
    <Card className="property-card h-100">
      <div className="property-image-container">
        {displayImages.length > 1 ? (
          <Carousel
            activeIndex={activeIndex}
            onSelect={handleSelect}
            interval={null}
            indicators={displayImages.length <= 5}
            controls={true}
            prevIcon={<BsChevronLeft size={20} />}
            nextIcon={<BsChevronRight size={20} />}
          >
            {displayImages.map((image, index) => (
              <Carousel.Item key={index}>
                <img
                  className="d-block w-100"
                  src={image}
                  alt={`${property.address.street} - Image ${index + 1}`}
                  style={{ height: '250px', objectFit: 'cover' }}
                  onError={(e: any) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Property+Image';
                  }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        ) : (
          <Card.Img 
            variant="top" 
            src={displayImages[0]} 
            alt={`${property.address.street}, ${property.address.city}`}
            style={{ height: '250px', objectFit: 'cover' }}
            onError={(e: any) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Property+Image';
            }}
          />
        )}
        
        {/* Image counter */}
        {displayImages.length > 1 && (
          <div className="image-counter">
            {activeIndex + 1} / {displayImages.length}
          </div>
        )}
        
        {/* Match score badge */}
        {property.matchScore && (
          <Badge bg="success" className="match-badge">
            {property.matchScore}% Match
          </Badge>
        )}
        
        {/* Days on market badge */}
        {property.daysOnMarket !== undefined && property.daysOnMarket <= 7 && (
          <Badge bg="danger" className="new-listing-badge">
            New Listing
          </Badge>
        )}
        
        {/* Save button */}
        <Button
          variant="light"
          className="save-btn"
          onClick={() => onSave(property.id)}
        >
          {isSaved ? (
            <BsHeartFill color="red" />
          ) : (
            <BsHeart />
          )}
        </Button>
      </div>
      
      <Card.Body>
        <h3 className="price">${property.price.toLocaleString()}</h3>
        <p className="address">
          {property.address.street}<br />
          {property.address.city}, {property.address.state} {property.address.zip}
        </p>
        <div className="property-features">
          <span>
            <BsHouseDoor /> {property.bedrooms} beds
          </span>
          <span>
            <BsDropletFill /> {property.bathrooms} baths
          </span>
          <span>
            <BsRulers /> {property.sqft.toLocaleString()} sqft
          </span>
        </div>
        
        {/* Match score explanation tooltip */}
        {property.matchScore && (
          <div className="match-score-info">
            <small className="text-muted">
              Score based on: location, price range, features & freshness
            </small>
          </div>
        )}
        
        <Button 
          variant="primary" 
          className="view-btn w-100 mt-3"
          onClick={() => onView(property.id)}
        >
          View Details
        </Button>
      </Card.Body>
    </Card>
  );
};

export default PropertyCardWithCarousel;